import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Settings, Plus, X, Loader2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import axios from 'axios';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CustomerPDPPartnerFunction = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('customer_pdp_partner_function');
  const [viewItem, setViewItem] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [partnerFunctions, setPartnerFunctions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentAssignment, setCurrentAssignment] = useState(null);
  const [formData, setFormData] = useState({
    procedure_id: '',
    partner_function_id: '',
    not_modifiable: false,
    mandatory: false
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [assignmentsRes, proceduresRes, functionsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/customer-pdp-partner-functions`),
        axios.get(`${BACKEND_URL}/api/customer-partner-determination-procedures`),
        axios.get(`${BACKEND_URL}/api/customer-partner-functions`)
      ]);
      
      setAssignments(assignmentsRes.data);
      setProcedures(proceduresRes.data);
      setPartnerFunctions(functionsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.procedure_id || !formData.partner_function_id) {
      toast.error('Please select both Procedure and Partner Function');
      return;
    }

    try {
      if (editMode) {
        await axios.put(
          `${BACKEND_URL}/api/customer-pdp-partner-functions/${currentAssignment.assignment_id}`,
          { 
            not_modifiable: formData.not_modifiable,
            mandatory: formData.mandatory
          }
        );
        toast.success('Assignment updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/customer-pdp-partner-functions`, formData);
        toast.success('Assignment created successfully');
      }
      
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      console.error('Error saving assignment:', error);
      toast.error(error.response?.data?.detail || 'Failed to save assignment');
    }
  };

  const handleEdit = (assignment) => {
    setCurrentAssignment(assignment);
    setFormData({
      procedure_id: assignment.procedure_id,
      partner_function_id: assignment.partner_function_id,
      not_modifiable: assignment.not_modifiable,
      mandatory: assignment.mandatory
    });
    setEditMode(true);
    setShowDialog(true);
  };

  const resetForm = () => {
    setFormData({ 
      procedure_id: '', 
      partner_function_id: '',
      not_modifiable: false,
      mandatory: false
    });
    setCurrentAssignment(null);
    setEditMode(false);
  };

  const handleDialogClose = () => {
    setShowDialog(false);
    resetForm();
  };

  const getProcedureName = (id) => {
    const proc = procedures.find(p => p.procedure_id === id);
    return proc?.name || id;
  };

  const getPartnerFunctionName = (id) => {
    const pf = partnerFunctions.find(p => p.partner_function_id === id);
    return pf?.name || id;
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Settings className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Procedure Partner Functions</h1>
                <p className="text-sm text-gray-600">Assign partner functions to determination procedures</p>
              </div>
            </div>
            {writable && (<Button
              onClick={() => setShowDialog(true)}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Assignment
            </Button>)}
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16">
              <Loader2 className="w-10 h-10 animate-spin text-emerald-600 mb-3" />
              <p className="text-sm text-gray-600">Loading assignments...</p>
            </div>
          ) : assignments.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Settings className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-1">No assignments yet</h3>
              <p className="text-sm text-gray-600 mb-4">
                Assign partner functions to procedures to define partner determination rules
              </p>
              {writable && (<Button onClick={() => setShowDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Assignment
              </Button>)}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Procedure ID</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Procedure Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Partner Function</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Function Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mandatory</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Not Modifiable</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {assignments.map((assignment) => (
                    <tr key={assignment.assignment_id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-gray-900">{assignment.procedure_id}</span>
                      </td>
                      <td className="px-6 py-4">{getProcedureName(assignment.procedure_id)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="font-medium text-emerald-900">{assignment.partner_function_id}</span>
                      </td>
                      <td className="px-6 py-4">{getPartnerFunctionName(assignment.partner_function_id)}</td>
                      <td className="px-6 py-4">
                        {assignment.mandatory ? (
                          <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded">Yes</span>
                        ) : (
                          <span className="text-gray-400 text-sm">No</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {assignment.not_modifiable ? (
                          <span className="px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded">Yes</span>
                        ) : (
                          <span className="text-gray-400 text-sm">No</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <RecordActions doctype="customer_pdp_partner_function" onView={() => setViewItem(assignment)} onEdit={() => handleEdit(assignment)} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create/Edit Dialog */}
        {showDialog && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editMode ? 'Edit Assignment' : 'Add Assignment'}
                </h2>
                <button onClick={handleDialogClose} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label>Procedure *</Label>
                  <select
                    value={formData.procedure_id}
                    onChange={(e) => setFormData({ ...formData, procedure_id: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                    disabled={editMode}
                  >
                    <option value="">Select Procedure</option>
                    {procedures.map(proc => (
                      <option key={proc.procedure_id} value={proc.procedure_id}>
                        {proc.procedure_id} - {proc.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <Label>Partner Function *</Label>
                  <select
                    value={formData.partner_function_id}
                    onChange={(e) => setFormData({ ...formData, partner_function_id: e.target.value })}
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md"
                    disabled={editMode}
                  >
                    <option value="">Select Partner Function</option>
                    {partnerFunctions.map(pf => (
                      <option key={pf.partner_function_id} value={pf.partner_function_id}>
                        {pf.partner_function_id} - {pf.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="mandatory"
                      checked={formData.mandatory}
                      onChange={(e) => setFormData({ ...formData, mandatory: e.target.checked })}
                      className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <Label htmlFor="mandatory" className="cursor-pointer">
                      Mandatory (Required in customer master)
                    </Label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="not_modifiable"
                      checked={formData.not_modifiable}
                      onChange={(e) => setFormData({ ...formData, not_modifiable: e.target.checked })}
                      className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <Label htmlFor="not_modifiable" className="cursor-pointer">
                      Not Modifiable (Cannot be changed in customer master)
                    </Label>
                  </div>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="text-xs text-orange-700">
                    <strong>Note:</strong> These flags control how the partner function behaves in the customer master data entry.
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={handleDialogClose} className="flex-1">
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                    {editMode ? 'Update' : 'Create'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default CustomerPDPPartnerFunction;
